import { NQError, NQErrorScheme, isNqError } from "@visonum/network-quality-sdk";
import sentryObject from "./sentryObject";
import { SeverityLevel } from "@sentry/nextjs";
import { SentryTags } from "./types";
import { getErrorNumberFromScheme, getNqErrorKind, isClientError, isConnectivityError, isServerError } from "../errors/errorUtils";
import * as R from "ramda";

export const sentryCaptureError = (error: NQError) => {
    const tags = getTagsForError(error);

    sentryObject.captureException(error, scope => {
        scope.setTags(tags);
        if (isNqError(error)) {
            scope.setLevel(errorToSeverityLevel(error));
        }
        return scope;
    });
}

const getTagsForError = (error: Error | NQError, prefix: string | null = null): SentryTags => {
    const result: SentryTags = {};

    if (isNqError(error)) {
        const nqError = error as NQError;
        result[createKey("ErrorScheme", prefix)] = nqError.scheme;
        result[createKey("ErrorNumber", prefix)] = getErrorNumberFromScheme(nqError.scheme);
        result[createKey("ErrorName", prefix)] = "NQError";

        result[createKey("NqCode", prefix)] = errorCodeToString(nqError.scheme);
        const nqInternalCode = errorInternalCodeToString(nqError.scheme);
        if (nqInternalCode !== null) {
            result[createKey("NqInternalCode", prefix)] = nqInternalCode;
        }
    } else {
        const _error = error as Error;
        result[createKey("ErrorNumber", prefix)] = 1000;
        result[createKey("ErrorName", prefix)] = _error.name;
        if (_error.cause instanceof Error) {
            const causeTags = getTagsForError(_error.cause, createKey("ErrorCause", prefix));
            Object.keys(causeTags).forEach(key => {
                result[key] = causeTags[key];
            });
        }    
    }

        
    result[createKey("ErrorMessage", prefix)] = error.message;

    return result;
}

export const createKey = (keyName: string, prefix: string | null): string => {
    if (prefix === null) {
        return keyName;
    }

    return `${prefix}.${keyName}`;
}

// TODO: to be removed
const errorCodeToString = (scheme: NQErrorScheme): string => {
    if (isServerError(scheme)) {
        return "Server";
    } else if (isConnectivityError(scheme)) {
        return "Connectivity";
    } else if (isClientError(scheme)) {
        return "Client";
    } else if (getNqErrorKind(scheme) === "System under maintenance") {
        return "SystemUnderMaintenance";
    } else if (getNqErrorKind(scheme) === "Rate limit") {
        return "RateLimit";
    } else if (getNqErrorKind(scheme) === "Parallel speedtests") {
        return "ParallelSpeedtests";
    } else {
        return "Unknown";
    }
}

// TODO: to be removed
const errorInternalCodeToString = (scheme: NQErrorScheme): string | null => {
    switch (scheme) {
        // NQInternalErrorCode.RemotePortDetection
        case "Rp-Cn-1-101":
        case "Rp-Cn-2-101":
            return "RemotePortDetection";

        // NQInternalErrorCode.MissingNQValueDuringSave
        case "Cl-6-132":
            return "MissingNQValueDuringSave";

        // NQInternalErrorCode.IllegalBenchmarkResultsValues: 
        case "Bm-1-133":
            return "IllegalBenchmarkResultsValues";

        // NQInternalErrorCode.DownloadError:
        case "Dn-Cn-1-164":
            return "DownloadError";

        // NQInternalErrorCode.DownloadTimeout:
        case "Dn-Cn-2-165":
            return "DownloadTimeout";

        // NQInternalErrorCode.UploadError:
        case "Up-Cn-1-166":
            return "UploadError";
        
        // NQInternalErrorCode.UploadTimeout:
        case "Up-Cn-2-167":
        case "Up-Cn-3-167":
            return "UploadTimeout";

        default:
            return null;
    }
}

export const errorToSeverityLevel = (error: NQError): SeverityLevel => {
    switch (error.scheme) {
        case "Sm-1-190":
        case "Rl-1-220":
            return "info";
        case "Ps-1-250":
            return "log";
        case "Rp-Cn-1-101":
        case "Rp-Cn-2-101":
            return "log";
        default:
            return "error";
    }
}

export default sentryCaptureError;